import { StorableBaseService, FormField, PaginatedResponse } from './_base';

import { employeeGroupService, EmployeeGroup } from './employee-groups';
import { physicalTokenService, PhysicalToken } from './physical-tokens';
import { institutionService } from './institutions';

import branding from '../branding';
import { AxiosResponse } from 'axios';
import { getRemoteOrLocal } from '../globals/cache';
import { institutionStore } from '@/store/institutions';
import { session } from '@/globals/session';
import { projectStore } from '@/store/projects';

interface Employee {
    id: string;
    created_at: string;
    modified_at: string;
    archived_at?: string;
    username: string;
    user_account_id: string;
    employee_group_id?: string;
    external_id?: string;
    accounting_number?: string;
    employee_group_name?: string;
    is_male: boolean;
    first_name: string;
    last_name: string;
    email?: string;
    initial_password: string;
    physical_token_id?: string;
    terminal_pin: string;
    can_be_deleted: boolean;
    delete_after_archive_period?: number;
    working_time_balance?: number;
    absence_count?: number;
    additional_fields: any;
}

interface EmployeesListParams {
    page?: number;
    pagesize?: number;
    query?: string;
    useCache?: boolean;

    verbosity?: string;

    token?: Array<string>;

    ids?: Array<string>;
    employeeGroups?: Array<string>;
    showArchived?: boolean;
    showAll?: "true" | "false" | "all";
    minimumAbsenceCount?: number;
    absenceMissingTypes?: Array<string>;
    absenceDaterange?: string;
}


class EmployeeService extends StorableBaseService<Employee> {
    archivedFormFields: Array<FormField<any>>;

    constructor() {
        super("/api/v2/employees/employees/");
        this.formFields = this.getFormFields();

        this.archivedFormFields = [
            new FormField<Employee>("first_name", "Vorname", {isReadOnly: true}),
            new FormField<Employee>("last_name", "Nachname", { isReadOnly: true }),
            new FormField<Employee>("email", "E-Mail-Adresse", { isReadOnly: true }),
            new FormField<EmployeeGroup>("employee_group_id", "Abteilung", {
                type: 'select',
                remoteSourceService: employeeGroupService,
                remoteSourceAttribute: "name",
                isReadOnly: true,
            }),
            new FormField<Employee>("accounting_id", "Personalnummer", { description: "Die Personalnummer wird verwendet, um den Mitarbeiter im Buchhaltungsexport zu identifizieren." , isReadOnly: true}),
        ];
        if (branding.id == "orange" || branding.id == 'black')
            this.archivedFormFields.push(new FormField<Employee>("external_id", "aQrate Benutzername", { description: "Der Benutzername wird verwendet, um diesen Zeitblick Mitarbeiter einem aQrate Mitarbeiter bei Authentifizierung am MFP zuzuordnen.", isReadOnly: true }));

        this.cacheTimeout = 60;
    }

    getFormFields() {
        const settings = institutionStore.settings();
        let terminalPinLength: undefined|number;
        if (settings?.terminals?.terminal_pin) {
            terminalPinLength = parseInt(settings.terminals.terminal_pin.split('max_')[1]);
        }

        let fields = [
            new FormField<Employee>("first_name", "Vorname", { autofocus: true }),
            new FormField<Employee>("last_name", "Nachname"),
            new FormField<Employee>("email", "E-Mail-Adresse (optional)", {
                mobileType: "text",
                description: "Die E-Mail-Adresse wird verwendet, um die initialen Zugangsdaten und weitere Kommunikation zu übermitteln.",
            }),
            new FormField<EmployeeGroup>("employee_group_id", "Abteilung (optional)", {
                type: 'remote-select',
                labelSingular: 'Abteilung',
                displayAttribute: 'name',
                allowNull: true,
                list: async (currentPage: number, pageSize: number, query: string) => {
                    return (await employeeGroupService.list(currentPage, pageSize, query)).data;
                },
                retrieveLabel: async (id?: string) => {
                    return id ? (await employeeGroupService.retrieve(id)).data.name : 'Keine Abteilung';
                },
                // type: 'select',
                // remoteSourceService: employeeGroupService,
                // remoteSourceAttribute: "name"
            }),
            new FormField<PhysicalToken>("physical_token_id", "Token-Nummer (optional)", {
                type: 'remote-select',
                description: "Die ausgewählte Token-Nummer kann zur Authentifizierung am Zeiterfassungs-Terminal verwendet werden. Nur nicht bereits zugewiesene Nummern werden angezeigt. <a target='_blank' class='text-primary' href='/tokens/'>Token verwalten</a>",
                labelSingular: 'Token-Nummer',
                displayAttribute: 'id',
                allowNull: true,
                list: async (currentPage: number, pageSize: number, query: string, currentValue?: string) => {
                    return (await physicalTokenService.listAvailable(currentPage, pageSize, query, currentValue)).data;
                },
                retrieveLabel: async (id?: string) => {
                    return id ? id : 'Kein Token';
                },
            }),
            new FormField("project_id", "Standardlabel (optional)", {
                type: 'project',
                isVisible: () => {
                    const permissions = session.getAccount().permissions;
                    if (permissions.indexOf('timespans:assign-projects') == -1) return false;

                    if (projectStore.count() == 0) return false;

                    return true;
                },
            }),
            new FormField<Employee>("terminal_pin", "Mitarbeiter-PIN (optional)", {
                type: 'text',
                description: 'Die PIN kann zur Authentifizierung an unterstützten Terminals verwendet werden.',
                isVisible: (employee) => {
                    return settings?.terminals?.terminal_pin && settings.terminals.terminal_pin != 'disabled';
                },
                setValue: (resource, value) => {
                    if (!settings?.terminals?.terminal_pin || settings.terminals.terminal_pin == 'disabled') {
                        resource.terminal_pin = '';
                    }
                    resource.terminal_pin = value.replace(/\D/g, '').slice(0, terminalPinLength);
                },
            }),
            new FormField<Employee>("accounting_number", "Personalnummer (optional)", { mobileType: 'text', description: "Die Personalnummer wird verwendet, um den Mitarbeiter im Buchhaltungsexport zu identifizieren." }),
        ];
        if (branding.id == "orange" || branding.id == "black")
            fields.push(new FormField<Employee>("external_id", "aQrate Benutzername (optional)", { mobileType: 'text', description: "Der Benutzername wird verwendet, um diesen Zeitblick Mitarbeiter einem aQrate Mitarbeiter bei Authentifizierung am MFP zuzuordnen." }));

        fields = fields.concat([
            new FormField<Employee>("username", branding.name + " Benutzername", {
                mobileType: 'text',
                description: "Der Benutzername wird vom Mitarbeiter benötigt, um sich gegenüber der " + branding.name + " Smartphone App und dem Web-Login (https://" + branding.url + "/) zu authentifizieren.",
                isReadOnly: true,
                showCreate: false,
            }),
            new FormField<Employee>("initial_password", "Vorläufiges Passwort", { mobileType: 'text', isReadOnly: true, showCreate: false, }),
        ]);

        fields = fields.concat(this.getAdditionalFormFields());

        return fields
    }

    titleAttribute(): Promise<any> { return Promise.resolve((resource: any) => resource.first_name + " " + resource.last_name) }
    newResourceTitle(): Promise<string> { return Promise.resolve("Mitarbeiter hinzufügen") }
    newResourceConfirmation(): Promise<string> { return Promise.resolve("Mitarbeiter hinzugefügt") }

    deleteResourceMethod(resourceId?: string): Promise<string> {
        if (!resourceId) return Promise.resolve('');

        return this.retrieve(resourceId).then(employeeResponse => {
            if (employeeResponse.data.can_be_deleted) {
                return Promise.resolve("delete");
            } else if (!employeeResponse.data.archived_at) {
                return Promise.resolve("archive");
            }

            return Promise.resolve('');
        });
    }
    deleteResourceTitle(resourceId?: string): Promise<string> {
        if (!resourceId) return Promise.resolve("Entfernen");

        return this.retrieve(resourceId).then(employeeResponse => {
            if (employeeResponse.data.can_be_deleted) {
                return Promise.resolve("Entfernen");
            } else if (!employeeResponse.data.archived_at) {
                return Promise.resolve("Archivieren");
            }

            return Promise.resolve('');
        });
    }
    deleteResourceConfirmation(resourceId?: string): Promise<string> {
        if (!resourceId) return Promise.resolve("Mitarbeiter entfernt");

        return this.retrieve(resourceId).then(employeeResponse => {
            if (employeeResponse.data.can_be_deleted) {
                return Promise.resolve("Mitarbeiter entfernt");
            } else if (!employeeResponse.data.archived_at) {
                return Promise.resolve("Mitarbeiter archiviert");
            }

            return Promise.resolve('');
        });
    }

    async retrieveSettingValue(resourceId: string, categoryId: string, settingId: string, contextDateUtc?: string): Promise<any> {
        const response = await this.retrieveSettings(resourceId);
        if (!response) {
            return institutionService.retrieveSettingValue(categoryId, settingId, contextDateUtc);
        }

        const employeeSettings = response.data;
        if (!employeeSettings?.[categoryId]?.[settingId]) {
            return institutionService.retrieveSettingValue(categoryId, settingId, contextDateUtc);
        }

        if (!contextDateUtc) contextDateUtc = (new Date()).toISOString();

        for (const item of employeeSettings[categoryId][settingId]) {
            if (item.valid_from <= contextDateUtc && (!item.valid_before || contextDateUtc < item.valid_before)) {
                return item.value;
            }
        }

        return institutionService.retrieveSettingValue(categoryId, settingId, contextDateUtc);
    }

    retrieveSettings(resourceId: string, onFresh?: (result: AxiosResponse<any>) => void): Promise<AxiosResponse<any>> {
        // onFresh: cache-first re-render callback — cached settings are served
        // first, then this fires with the live server state (see getRemoteOrLocal).
        return getRemoteOrLocal<any>(
            'store/employees/employees/settings-historical',
            this._get.bind(this), resourceId + "/settings-historical/", onFresh);
    }

    updateSettings(resourceId: string, newSettings: any) {
        return this._post(resourceId + "/settings/", newSettings);
    }

    resetPassword(resourceId: string) {
        return this._post(resourceId + "/reset-password/", {});
    }

    workingTime(resourceId: string, daterange: string) {
        return this._get(resourceId + '/working-time/?daterange=' + daterange);
    }

    sendInvite(resourceId: string) {
        return this._post(resourceId + "/send-invite/", {}) as unknown as Promise<AxiosResponse<{success: boolean}>>;
    }

    list(page=1, pagesize=50, query='', employeeGroups?: string, showArchived?: boolean) {
        let params =
            '?limit=' + String(pagesize) +
            '&offset=' + String((page - 1) * pagesize) +
            '&q=' + encodeURIComponent(String(query));

        if (employeeGroups != undefined) {
            params = params + '&employee_groups=' + employeeGroups;
        }

        if (showArchived === true) {
            params = params + '&archived';
        }

        return this._get(params);
    }

    _getListParamsUrl(params: EmployeesListParams) {
        const pagesize = params.pagesize || 50;
        const page = params.page || 1;
        const query = params.query || '';

        let url =
            '?limit=' + String(pagesize) +
            '&offset=' + String((page - 1) * pagesize) +
            '&q=' + encodeURIComponent(String(query));

        if (params.verbosity) url += "&verbosity=" + params.verbosity;
        if (params.ids) url += "&ids=" + params.ids.join("|");
        if (params.token) url += "&token=" + params.token.join("|");
        if (params.employeeGroups) url += "&employee_groups=" + params.employeeGroups.join("|");
        if (params.showArchived === true) url = url + '&archived=true';
        if (params.showAll && !params.showArchived) url = url + '&archived=' + params.showAll;
        if (params.minimumAbsenceCount) url = url + '&absence_count__gte=' + String(params.minimumAbsenceCount);
        if (params.absenceMissingTypes) url = url + '&absence_missing_types=' + params.absenceMissingTypes.join("|");
        if (params.absenceDaterange) url = url + '&absence_daterange=' + params.absenceDaterange;

        return url;
    }

    listParams(params: EmployeesListParams): Promise<AxiosResponse<PaginatedResponse<Employee>>> {
        const url = this._getListParamsUrl(params);
        return this._get(url, params.useCache !== false);
    }

    archive(resourceId: string) {
        return this._post(resourceId + "/archive/", {});
    }

    restore(resourceId: string) {
        return this._post(resourceId + "/restore/", {});
    }
}

const employeeService = new EmployeeService();

export { EmployeeService, employeeService, Employee };

export default employeeService;
